import { isDefined } from "@omni/utils/helper.utils";
import { createMayBeFor, MaybeType } from "@omni/utils/maybe.utils";

import { Facet } from "@/components/Search/SearchResults/components/results/components/productsResults/components/facets/components/facet/@types/facet.types";
import { FacetType } from "@/components/Search/SearchResults/components/results/components/productsResults/components/facets/components/facet/@types/facetType.types";
import { getComponentState } from "@/redux/modules/value/search/search.selectors";
import { getHierarchicalFacetName } from "@/services/bffSearch/responseTransformers/mergeHierarchicalFacets.transformer";
import {
    MayBeConfigurationToRouteEntriesMapper,
    RouteEntry
} from "@/utils/search/routing/configurationToUrl/configurationToRouteEntries";
import {
    ALLOWED_NON_SEARCHABLE_FACETS,
    FACET_FILTER_SEPARATOR,
    NUMERIC_FILTER_SEPARATOR,
    PREFIX_SEPARATOR
} from "@/utils/search/routing/routing.configuration";
import {
    FacetFilter,
    FacetsConfiguration,
    NumericFilter
} from "@/utils/search/types/searchConfiguration";
import { FacetsState } from "@/utils/search/types/searchState";

export type Filters = {
    facetFilter: FacetFilter;
    numericFilter: NumericFilter;
};

export type SelectedFilterMapper<TFacetType extends FacetType> = (
    prefix: string,
    facetName: Facet<TFacetType>["name"],
    filters: Partial<Filters>
) => RouteEntry | undefined;

const defaultFacetMapper: SelectedFilterMapper<FacetType.DEFAULT> = (
    prefix,
    facetName,
    filters
) => {
    const value = filters.facetFilter?.selectedValues.join(
        FACET_FILTER_SEPARATOR
    );

    if (!value) return;
    return [`${prefix}${PREFIX_SEPARATOR}${facetName}`, value];
};

const numericFacetMapper: SelectedFilterMapper<FacetType.NUMERIC> = (
    prefix,
    facetName,
    filters
) => {
    if (!filters.numericFilter) return;

    const numericFilter = filters.numericFilter;
    const lowerBound = numericFilter.lowerBound ?? "*";
    const upperBound = numericFilter.upperBound ?? "*";

    return [
        `${prefix}${PREFIX_SEPARATOR}${facetName}`,
        `${lowerBound}${NUMERIC_FILTER_SEPARATOR}${upperBound}`
    ];
};

const hierarchicalFacetMapper: SelectedFilterMapper<FacetType.NUMERIC> = (
    prefix,
    facetName,
    filters
) => {
    const value = filters.facetFilter?.selectedValues.at(0);

    if (!value) return;
    return [`${prefix}${PREFIX_SEPARATOR}${facetName}`, value];
};

const facetTypeToMapperMap = {
    [FacetType.DEFAULT]: defaultFacetMapper,
    [FacetType.COLORSWATCH]: defaultFacetMapper,
    [FacetType.UNIT_RANGE]: defaultFacetMapper,
    [FacetType.NUMERIC]: numericFacetMapper,
    [FacetType.HIERARCHICAL]: hierarchicalFacetMapper,
    [FacetType.SHOW_PRODUCTS]: defaultFacetMapper
} as const;

const getMapperByFacetType = <TFacetType extends FacetType>(
    facetType: TFacetType
) => facetTypeToMapperMap[facetType] as SelectedFilterMapper<TFacetType>;

const getFacetForFilter = <TFilter extends keyof Filters>(
    filter: Filters[TFilter],
    facets: Facet<FacetType>[] | undefined
): Pick<Facet<FacetType>, "name" | "type"> | undefined => {
    // If the filter is known as a searchable facet, get it from the available facets
    const facetForFilter = facets?.find((facet) => {
        const facetFilterName =
            facet.type === FacetType.HIERARCHICAL
                ? getHierarchicalFacetName(filter.name)
                : filter.name;

        return facetFilterName === facet.name;
    });

    if (facetForFilter) {
        return {
            name: facetForFilter.name,
            type: facetForFilter.type
        };
    }

    // If the filter is not known as a searchable facet, only a list of default facets is allowed,
    // so we do not allow the client to query for arbitrary facets
    return ALLOWED_NON_SEARCHABLE_FACETS.find(
        ({ name }) => name === filter.name
    );
};

const mapFacetsConfiguration: MayBeConfigurationToRouteEntriesMapper = (
    configuration,
    prefix
) => {
    const mayBeFacetsConfiguration = createMayBeFor<FacetsConfiguration>(
        ["facetFilters", "numericFilters"],
        configuration
    );
    if (mayBeFacetsConfiguration.type === MaybeType.Nothing) return null;

    const facets = (
        getComponentState(configuration.component)(
            window.storeManager.getState()
        ) as Partial<FacetsState>
    )?.facets;

    const mapFilterToRouteEntry =
        <TFilter extends keyof Filters>(filterType: TFilter) =>
        (filter: Filters[TFilter]): RouteEntry | undefined => {
            const facetForFilter = getFacetForFilter(filter, facets);

            if (!facetForFilter) return;

            return getMapperByFacetType(facetForFilter.type)(
                prefix,
                facetForFilter.name,
                { [filterType]: filter }
            );
        };

    return [
        ...mayBeFacetsConfiguration.value.facetFilters.map(
            mapFilterToRouteEntry("facetFilter")
        ),
        ...mayBeFacetsConfiguration.value.numericFilters.map(
            mapFilterToRouteEntry("numericFilter")
        )
    ].filter(isDefined);
};

export { mapFacetsConfiguration };
